import React from "react";
import { Api } from "../../../api";
import { hasError } from "../../../helpers/ResponseHelper";
import Employee from "../../../types/Employee";
import MaterialTable from "material-table";
import { Chip, Button } from "@material-ui/core";
import UserAvatar from "../../UserAvatar";
import TableSearch from "../../Table/TableSearch";
import { useHistory } from "react-router-dom";
import EmployeePickerDialog from ".././EmployeePickerDialog";
import { useSnackbar } from "notistack";
import MoreButton from "../../MoreButton";
import BulkActionsButton from "../../../BulkActionsButton";

interface IProps {
  companyId: string;
  groupId: string;
}
export default function AdminGroupDetailsEmployees(props: IProps) {
  const { enqueueSnackbar } = useSnackbar();
  const { companyId, groupId } = props;
  const [employees, setEmployees] = React.useState<Employee[]>([]);
  const [filtered, setFiltered] = React.useState<Employee[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [showEmployeeSearchModal, setShowEmployeeSearchModal] = React.useState(false);
  const [showInactive, setShowInactive] = React.useState(false);
  const [disableBulkActions, setDisableBulkActions] = React.useState(true);
  const history = useHistory();
  const [selectedEmployees, setSelectedEmployees] = React.useState<Employee[]>(
    []
  );

  React.useEffect(() => {
    if (companyId && groupId) {
      setLoading(true);
      Api.groups
        .getEmployeesByGroup(companyId, groupId)
        .then((res) => {
          if (!hasError(res)) {
            setEmployees(res.data.data);
          }
        })
        .finally(() => setLoading(false));
    }
  }, [companyId, groupId]);

  React.useEffect(() => {
    if (employees) {
      setFiltered(
        employees.filter((employee) => {
          return showInactive ? true : employee.active;
        })
      );
    }
  }, [employees, showInactive]);

  function fetchEmployees() {
    if (companyId && groupId) {
      setLoading(true);
      Api.groups
        .getEmployeesByGroup(companyId, groupId)
        .then((res) => {
          if (!hasError(res)) {
            setEmployees(res.data.data);
          }
        })
        .finally(() => setLoading(false));
    }
  }

  function addEmployeesToGroup(newEmployees: Array<Employee>) {

    let employeeIds: Array<string> = [];

    if (employees) {
      newEmployees.forEach((employee) => {
        if ((employees.find(i => i.employeeId === employee.employeeId)) == null) {
          employeeIds.push(employee.employeeId);
        }
      });
    } else {
      employeeIds = newEmployees.map(employee => {
        return employee.employeeId
      })
    }

    if (employeeIds.length > 0) {
      Api.groups
        .addEmployeeListToGroup(
          props.companyId,
          groupId,
          employeeIds
        )
        .then((result) => {
          if (!hasError(result)) {
            enqueueSnackbar("Employees added to group successfully", {
              variant: "success",
            });
            fetchEmployees();
            setShowEmployeeSearchModal(false);
          }
        });
    } else {
      setShowEmployeeSearchModal(false);
    }
  }

  function removeEmployees(employees: Array<Employee>) {
    let employeeIds: Array<string> = [];
    employees.forEach((employee) => {
      employeeIds.push(employee.employeeId);
    });

    Api.groups
      .removeEmployeesFromGroup(
        props.companyId,
        groupId,
        employeeIds
      )
      .then((result) => {
        if (!hasError(result)) {
          enqueueSnackbar("Removed from group successfully", {
            variant: "success",
          });
          fetchEmployees();
        }
      });
  }

  function getOptions() {
    return ["Remove"];
  }

  function getBulkOptions() {
    return ["Remove"];
  }

  function actionClickHandler(value: string) {
    removeEmployees(selectedEmployees)
  }

  function moreHandler(menuItem: string, index: number, rowData: any) {
    switch (menuItem) {
      case "Remove":
        removeEmployees([rowData])
        break;
    }
  }

  function onSelectionChange(rows: any) {
    setDisableBulkActions(!(rows.length > 0));
    setSelectedEmployees(rows);
  }

  return (
    <div>
      <div className="datatable-container">
        <div className="buttons">
          <BulkActionsButton
            options={getBulkOptions()}
            clickHandler={actionClickHandler}
            disabled={disableBulkActions}
          />
          <div className="spacer" />
          <Button
            color="primary"
            variant="contained"
            onClick={() => {
              setShowEmployeeSearchModal(true);
            }}
          >
            Add Employee
          </Button>

          <Button
            color="primary"
            variant="contained"
            onClick={() => {
              setShowInactive((value) => !value);
            }}
          >
            {showInactive ? "Hide Inactive" : "Show Inactive"}
          </Button>
        </div>
        <MaterialTable
          options={{
            selection: true,
            filtering: false,
            debounceInterval: 500,
            pageSize: 20,
          }}
          onRowClick={(event: any, rowData?: Employee) => {
            if (rowData) history.push(`/profile/${rowData.employeeId}`);
          }}
          columns={[
            {
              title: "STATUS",
              field: "active",
              disableClick: true,
              render: (rowData) =>
                rowData.active ? (
                  <Chip label="Active" color="primary" className="chip" />
                ) : (
                    <Chip label="Inactive" className="chip" />
                  ),
            },
            {
              title: "NAME",
              field: "firstName",
              defaultSort: "asc",
              render: (rowData) => (
                <div style={{ display: "flex", alignItems: "center" }}>
                  <UserAvatar
                    type="table"
                    firstName={rowData.firstName}
                    lastName={rowData.lastName}
                  />{" "}
                  {rowData.firstName} {rowData.lastName}
                </div>
              ),
            },
            {
              title: "FIRST NAME",
              field: "firstName",
              defaultSort: "asc",
              hidden: true,
            },
            {
              title: "LAST NAME",
              field: "lastName",
              hidden: true,
            },
            {
              title: "USER NAME",
              field: "userName",
            },
            {
              title: "EMAIL",
              field: "primaryEmailAddress",
              disableClick: true,
            },
            { title: "PHONE", field: "primaryPhoneNumber", disableClick: true },
            {
              title: "",
              cellStyle: { textAlign: "right", width: "48px" },
              field: "employeeId",
              disableClick: true,
              render: (rowData) => (
                <MoreButton
                  rowData={rowData}
                  menuItems={getOptions()}
                  menuItemSelectedHandler={moreHandler}
                ></MoreButton>
              ),
            }
          ]}
          data={filtered}
          onSelectionChange={onSelectionChange}
          components={{
            Toolbar: (props) => <TableSearch toolbarProps={props} />,
          }}
          isLoading={loading}
        />
      </div>

      <EmployeePickerDialog
        onCancel={() => {
          setShowEmployeeSearchModal(false);
        }}
        onConfirm={(selectedEmployees: Array<Employee>) => {
          addEmployeesToGroup(selectedEmployees);
        }}
        showModal={showEmployeeSearchModal}
        companyId={props.companyId}
      />

    </div>
  );
}